import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  DailyLogType,
  DailyOperationLogRecord
} from '../../models/projects/operation.models';
import { ErrorType, ValidationResult } from '../../models/projects/validation.models';
import { UpdatedSetupSummary } from 'models/setup/setup.models';
import { DailyLogService } from '../daily-log/daily-log.service';
import { TimeZoneService } from '../time-zone/time-zone.service';
import { AppConfig } from '../../app/app.config';
import { DateTime } from 'luxon';
import { PtLuxonDate } from '../../models/projects/date.models';

@Injectable()
export class SetupValidatorService {
  config = AppConfig.settings;

  constructor(
    private http: HttpClient,
    private dailyLogService: DailyLogService,
    private timeZoneService: TimeZoneService,
  ) {

  }

  async checkNewerOperations(newDate: DateTime, stageId: number, validationResult: ValidationResult): Promise<ValidationResult> {
    const badLogs = await this.getNewerOperations(newDate, stageId);

    if (badLogs.length > 0) {
      badLogs.forEach(element => {
        if (element.typeId === DailyLogType.OperationStart) {
          validationResult.addError(`Operation '${element.operationName}
          ' will be removed from the daily log if you save this action`, ErrorType.Warning);
        }
      });
    }

    return validationResult;
  }

  async deleteNewerOperations(newDate: DateTime, stageId: number) {
    const badLogs = await this.getNewerOperations(newDate, stageId);

    if (badLogs.length > 0) {
      badLogs.forEach(element => {
        this.dailyLogService.deleteDailyLogRecord(element.id);
      });
    }
  }

  async getNewerOperations(newDate: DateTime, stageId: number): Promise<DailyOperationLogRecord[]> {
    const logs = await this.dailyLogService.getDailyLogData();

    const badLogs = logs
      .filter(p => p.sortTime > newDate
        && (p.operationInstanceId != null && p.stageIds
          ? p.stageIds.some(s => s === stageId)
          : false));

    const operationIds = badLogs
      .map(p => p.operationInstanceId)
      .filter((id, index, array) => array.indexOf(id) === index);

    const logsToDelete = logs
      .filter(p => operationIds
        .indexOf(p.operationInstanceId) > -1);

    return logsToDelete;
  }

  validateLocation(previousLocation: string, newLocation: string, validationResult: ValidationResult): ValidationResult {

    if (!previousLocation) {
      return;
    }

    const error = validationResult.errors.find(p => p.type === ErrorType.Warning);

    if (previousLocation !== newLocation && !error && validationResult.errors.length === 0) {
      validationResult.addError('Changing your location is is only advisable in exceptional circumstances', ErrorType.Warning);
    }

    if (previousLocation === newLocation && !!error) {
      validationResult.errors = validationResult.errors.filter(p => p.type !== ErrorType.Warning);
    }

    return validationResult;
  }

  async validateSetup(summary: UpdatedSetupSummary, ptLuxonDate: PtLuxonDate) {
    const validationResult = new ValidationResult();

    if (!summary) {
      return;
    }

    const invalid = await this.timeZoneService.isDateLostInTime(ptLuxonDate.dateTime);

    if (invalid.result) {
      validationResult.addError(`This time of change isn't allowed due to a timezone change`, ErrorType.Default);
    }

    if (ptLuxonDate.multipleOffsets && ptLuxonDate.multipleOffsets.result && !ptLuxonDate.multipleOffsets.selectedOffsetMinutes) {
      validationResult.addError(`You must select an offset for the Time of Change`, ErrorType.Default);
    }

    if (!summary.locationId) {
      validationResult.addError('You must choose a location', ErrorType.Default);
    }

    if (!summary.stageId) {
      validationResult.addError('You must choose a stage', ErrorType.Default);
    }

    if (!summary.phaseId) {
      validationResult.addError('You must choose a phase', ErrorType.Default);
    }

    if (!summary.projectId) {
      validationResult.addError('You must choose a project', ErrorType.Default);
    }

    if (!summary.vesselMasterName || this.stringNullOrEmpty(summary.vesselMasterName)
      && !validationResult.errors.some(e => e.message === 'You must enter a Vessel Master')) {
      validationResult.addError('You must enter a Vessel Master', ErrorType.Default);
    }

    return validationResult;
  }

  stringNullOrEmpty(str: string) {
    return str === null || str.match(/^ *$/) !== null;
  }

}



// WEBPACK FOOTER //
// ./src/services/setup/setup-validator.service.ts